// ============================================================================
// FSA NOTE MODAL — SHARED LINE ITEM EDITOR
// ============================================================================
// Inline form for adding / renaming line items beneath a class. Hosts the
// SAME visual chrome the standard 2-col pane has always used, extracted
// from MainNotePane so the multi-column pane (PPE / Intangibles / future
// Partnerships) can drive the IDENTICAL editor — same source picker, same
// ledger chip-multi-select, same manual amount inputs, same dimension /
// cross-note sub-sections.
//
// Multi-column callers pass an `extraColumns` array (FormatColumnSpec[])
// — one row per column is rendered between the manual-amount section and
// the dimension/cross-note sub-sections, with NumericInputs that read /
// write the auditor's per-column working detail. Computed columns
// (is_computed) are rendered read-only with a small `ƒ` chip; saving
// posts both the typed inputs and the computed-companion values.

import React, { useCallback, useMemo, useState } from 'react';
import { NumericInput } from '../../shared/NumericInput';
import type {
  NoteCrossNoteConfig,
  NoteCrossNoteTarget,
  NoteGroupingDimension,
} from 'api/types/fsa/noteData.types';

// ----------------------------------------------------------------------
// Public draft + entity types
// ----------------------------------------------------------------------

/**
 * Ledger row the editor shows in its picker. Structurally compatible
 * with `NoteHydrate['liveClasses'][number]['ledgers'][number]` so callers
 * can pass live ledgers straight through. `ledgerId` accepts the wider
 * ID union (string | number) the rest of the codebase uses; the picker
 * stringifies it for use as a stable Set key.
 */
export interface LineItemEditorLedger {
  ledgerId: string | number;
  ledgerName: string;
  closingActual: number;
  openingActual: number;
}

/**
 * Canonical draft shape — mirrors the standard pane's `LineItemDraft` 1:1.
 * `allocation_mode` accepts the wider line-item union so callers can hand
 * over a draft built from a saved cross_note / pl_flow row without
 * coercion; the editor clamps to 'ledgers' | 'manual' internally and the
 * save payload narrows back to those two modes.
 */
export interface LineItemDraft {
  name: string;
  allocation_mode: 'ledgers' | 'manual' | 'cross_note' | 'pl_flow';
  ledger_ids: string[];
  manual_amount_actual: number;
  manual_prior_amount_actual: number;
  /**
   * Workspace dimension_values keyed by dimension code. The editor reads
   * the active groupingDimension's value (if any) into local state and
   * writes it back on save. Keys for other dimensions pass through
   * unchanged so multi-dimension schedules degrade gracefully.
   */
  dimension_values: Record<string, string>;
  cross_note_amount: number;
  prior_cross_note_amount: number;
  cross_note_target: NoteCrossNoteTarget | null;
}

export const blankLineItemDraft = (): LineItemDraft => ({
  name: '',
  allocation_mode: 'ledgers',
  ledger_ids: [],
  manual_amount_actual: 0,
  manual_prior_amount_actual: 0,
  dimension_values: {},
  cross_note_amount: 0,
  prior_cross_note_amount: 0,
  cross_note_target: null,
});

// ----------------------------------------------------------------------
// LineItemEditor
// ----------------------------------------------------------------------

interface Props {
  className: string;
  ledgers: LineItemEditorLedger[];
  /** Ledger ids referenced by OTHER line items on this class — filtered out of the picker so we don't double-allocate. */
  referencedLedgerIds: Set<string>;
  /** Set when in 'edit' mode — drives the Save button copy ("Save" vs "Add") and ledger filtering. */
  editingLocalId: string | null;
  draft: LineItemDraft;
  /** TRUE for credit-balance schedules — flips display sign at the input cell. Stored values stay signed. */
  invertSign?: boolean;
  /**
   * Sub-head grouping dimension. When set, the editor renders a Select
   * for the dimension value (Secured / Unsecured etc.). NULL = hide.
   */
  groupingDimension?: NoteGroupingDimension | null;
  /**
   * Template-level cross-note config. When set, the cross-note CY/PY
   * inputs appear and the target schedule/class/label are auto-resolved
   * from the config on save. NULL = hide cross-note entirely.
   */
  crossNoteConfig?: NoteCrossNoteConfig | null;
  /**
   * FSA-2.2 — TRUE when the parent class has class_role = 'MEMO_ONLY'
   * (e.g. Authorised Capital on Note 1). MEMO_ONLY classes can't accept
   * TB ledger mappings — line items under them must be manual-amount
   * only. When set, the editor:
   *   • Forces allocation_mode = 'manual'.
   *   • Hides the source-mode toggle (no "From ledger(s)" option).
   *   • Hides the ledger picker entirely.
   */
  forceManualOnly?: boolean;
  /**
   * FSA-2.2 — TRUE for class-level cross-note materialized lines (auto
   * sign-sweep target lines: Advances to Suppliers, Advances from
   * Customers). The source is class-level on the upstream Note, so
   * there's no source line item the auditor can rename — they rename
   * the materialized line directly here. When set, only the Name field
   * is editable; every other field (allocation mode, amounts, ledger
   * picker, dimension, cross-note inputs) renders read-only / hidden.
   * Saved name persists through the next "Update from cross-notes"
   * refresh (saved-snapshot-wins merge in FsaNoteModal).
   */
  forceRenameOnly?: boolean;
  onCancel: () => void;
  onSave: (draft: LineItemDraft) => void;
}

const LineItemEditor: React.FC<Props> = ({
  className,
  ledgers,
  referencedLedgerIds,
  editingLocalId,
  draft,
  invertSign,
  groupingDimension,
  crossNoteConfig,
  forceManualOnly,
  forceRenameOnly,
  onCancel,
  onSave,
}) => {
  const [name, setName] = useState(draft.name);
  const [mode, setMode] = useState<'ledgers' | 'manual'>(
    // Defensive — cross_note / pl_flow are read-only in tables and
    // shouldn't reach the editor; clamp to the two modes we render.
    // MEMO_ONLY parents force 'manual' regardless of the draft's mode.
    forceManualOnly
      ? 'manual'
      : draft.allocation_mode === 'ledgers' || draft.allocation_mode === 'manual'
        ? draft.allocation_mode
        : 'manual',
  );
  const [ledgerIds, setLedgerIds] = useState<Set<string>>(
    () => new Set(draft.ledger_ids),
  );
  const [dimensionValue, setDimensionValue] = useState<string>(
    () => (groupingDimension ? (draft.dimension_values[groupingDimension.code] ?? '') : ''),
  );
  const [manualCy, setManualCy] = useState<number>(draft.manual_amount_actual);
  const [manualPy, setManualPy] = useState<number>(draft.manual_prior_amount_actual);
  const [crossNoteCy, setCrossNoteCy] = useState<number>(draft.cross_note_amount ?? 0);
  const [crossNotePy, setCrossNotePy] = useState<number>(draft.prior_cross_note_amount ?? 0);

  const sign = invertSign ? -1 : 1;

  const isValid =
    name.trim().length > 0 &&
    (mode === 'manual' ? true : ledgerIds.size > 0);

  const draftOwnIds = useMemo(() => new Set(draft.ledger_ids), [draft.ledger_ids]);
  const availableLedgers = useMemo(
    () => ledgers.filter(l => {
      const id = String(l.ledgerId);
      return !referencedLedgerIds.has(id) || draftOwnIds.has(id);
    }),
    [ledgers, referencedLedgerIds, draftOwnIds],
  );

  const selectedSum = useMemo(() => {
    let cy = 0, py = 0;
    for (const l of ledgers) {
      if (ledgerIds.has(String(l.ledgerId))) {
        cy += l.closingActual;
        py += l.openingActual;
      }
    }
    return { cy, py };
  }, [ledgers, ledgerIds]);

  return (
    <div style={editorContainerStyle}>
      <div style={editorTitleStyle}>
        {editingLocalId ? `Edit line item — ${className}` : `Add line item — ${className}`}
      </div>

      <div style={editorRowStyle}>
        <label style={editorLabelStyle}>Name <span style={requiredStarStyle}>*</span></label>
        <input
          type="text"
          className="form-input"
          value={name}
          onChange={e => setName(e.target.value)}
          placeholder="e.g. HDFC TL Tranche A"
          style={editorInputStyle}
          autoFocus
        />
      </div>

      {/* Class-level cross-note materialized lines (sign-sweep targets:
          Advances to Suppliers / Advances from Customers) — auditor can
          only rename. Source / amounts / dimension / cross-note inputs
          are hidden; values stay as saved and pass through unchanged on
          save. */}
      {forceRenameOnly && (
        <div style={renameOnlyHintStyle}>
          Amounts on this line are auto-computed from the source Note's
          sign sweep. Only the name can be edited.
        </div>
      )}

      {/* MEMO_ONLY parents skip the source-mode toggle — only manual
          input is allowed (no ledger picker, no cross-note). */}
      {!forceManualOnly && !forceRenameOnly && (
        <div style={editorRowStyle}>
          <label style={editorLabelStyle}>Source</label>
          <div style={{ display: 'flex', gap: 12, fontSize: '0.8rem' }}>
            <label style={radioLabelStyle}>
              <input
                type="radio"
                checked={mode === 'ledgers'}
                onChange={() => setMode('ledgers')}
              />
              From ledger(s)
            </label>
            <label style={radioLabelStyle}>
              <input
                type="radio"
                checked={mode === 'manual'}
                onChange={() => setMode('manual')}
              />
              Manual amount
            </label>
          </div>
        </div>
      )}

      {!forceRenameOnly && (mode === 'ledgers' ? (
        <div style={editorRowStyle}>
          <label style={editorLabelStyle}>
            Ledgers <span style={requiredStarStyle}>*</span>
          </label>
          <LedgerChipPicker
            availableLedgers={availableLedgers}
            selectedIds={ledgerIds}
            onChange={setLedgerIds}
            invertSign={!!invertSign}
            selectedSum={selectedSum}
          />
        </div>
      ) : (
        <>
          <div style={editorRowStyle}>
            <label style={editorLabelStyle}>Amount (CY)</label>
            <NumericInput
              className="form-input"
              value={manualCy * sign}
              onChange={v => setManualCy(v * sign)}
              style={editorInputStyle}
            />
          </div>
          <div style={editorRowStyle}>
            <label style={editorLabelStyle}>Amount (PY)</label>
            <NumericInput
              className="form-input"
              value={manualPy * sign}
              onChange={v => setManualPy(v * sign)}
              style={editorInputStyle}
            />
          </div>
        </>
      ))}

      {!forceRenameOnly && groupingDimension && (
        <div style={editorRowStyle}>
          <label style={editorLabelStyle}>{groupingDimension.label}</label>
          <select
            className="form-input"
            value={dimensionValue}
            onChange={e => setDimensionValue(e.target.value)}
            style={editorInputStyle}
            title={`Pick a ${groupingDimension.label} for this line item. Leave empty for Untagged.`}
          >
            <option value="">— Untagged —</option>
            {groupingDimension.options.map(opt => (
              <option key={opt.code} value={opt.code}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>
      )}

      {!forceRenameOnly && crossNoteConfig && (
        <div style={crossNoteSectionStyle}>
          <div style={editorRowStyle}>
            <label style={editorLabelStyle}>{crossNoteConfig.label} (CY)</label>
            <NumericInput
              className="form-input"
              value={crossNoteCy * sign}
              onChange={v => setCrossNoteCy(v * sign)}
              style={editorInputStyle}
            />
          </div>
          <div style={editorRowStyle}>
            <label style={editorLabelStyle}>{crossNoteConfig.label} (PY)</label>
            <NumericInput
              className="form-input"
              value={crossNotePy * sign}
              onChange={v => setCrossNotePy(v * sign)}
              style={editorInputStyle}
            />
          </div>
        </div>
      )}

      <div style={editorFooterStyle}>
        <button type="button" className="btn btn-secondary btn-sm" onClick={onCancel}>
          Cancel
        </button>
        <button
          type="button"
          className="btn btn-primary btn-sm"
          disabled={!isValid}
          onClick={() => {
            // Rename-only mode (class-level cross-note materialized line):
            // only the name is editable; pass every other field through
            // from the original draft so the saved snapshot still wins on
            // the next "Update from cross-notes" merge.
            if (forceRenameOnly) {
              onSave({
                ...draft,
                name,
              });
              return;
            }
            const dimensionValues = { ...draft.dimension_values };
            if (groupingDimension) {
              if (dimensionValue) {
                dimensionValues[groupingDimension.code] = dimensionValue;
              } else {
                delete dimensionValues[groupingDimension.code];
              }
            }
            // Attach the destination pointer when EITHER period carries a
            // routed amount. Gating on CY alone dropped the target whenever
            // the current year was nil (e.g. Long-term Borrowings → Short-term
            // Borrowings where the current maturity exists only in the
            // comparative), so the flow never reached the destination Note.
            // The aggregator applies the same either-period rule server-side.
            const crossNoteTarget: NoteCrossNoteTarget | null =
              (crossNoteCy !== 0 || crossNotePy !== 0)
                ? draft.cross_note_target ??
                  (crossNoteConfig
                    ? {
                        schedule_code: crossNoteConfig.target_schedule_code,
                        class_code: crossNoteConfig.target_class_code ?? null,
                        label: crossNoteConfig.label,
                      }
                    : null)
                : null;
            onSave({
              name,
              allocation_mode: mode,
              ledger_ids: Array.from(ledgerIds),
              manual_amount_actual: manualCy,
              manual_prior_amount_actual: manualPy,
              dimension_values: dimensionValues,
              cross_note_amount: crossNoteCy,
              prior_cross_note_amount: crossNotePy,
              cross_note_target: crossNoteTarget,
            });
          }}
        >
          Save
        </button>
      </div>
    </div>
  );
};

// ----------------------------------------------------------------------
// LedgerChipPicker — chip multi-select with native-<select> add. Extracted
// from MainNotePane verbatim so the visual + behaviour matches exactly.
// Why native <select>: deep modals (engagement → Note Modal → editor)
// trap focus + create stacking contexts that break portal-based dropdowns.
// Native renders outside the React tree and just works.
// ----------------------------------------------------------------------

const LedgerChipPicker: React.FC<{
  availableLedgers: LineItemEditorLedger[];
  selectedIds: Set<string>;
  onChange: (next: Set<string>) => void;
  invertSign: boolean;
  selectedSum: { cy: number; py: number };
}> = ({ availableLedgers, selectedIds, onChange, invertSign, selectedSum }) => {
  const sign = invertSign ? -1 : 1;

  const selectedLedgers = useMemo(
    () => availableLedgers.filter(l => selectedIds.has(String(l.ledgerId))),
    [availableLedgers, selectedIds],
  );
  const unselectedLedgers = useMemo(
    () => availableLedgers.filter(l => !selectedIds.has(String(l.ledgerId))),
    [availableLedgers, selectedIds],
  );

  const removeLedger = useCallback((id: string) => {
    const next = new Set(selectedIds);
    next.delete(id);
    onChange(next);
  }, [selectedIds, onChange]);

  // ───── Inline multi-select panel state ──────────────────────────────
  // Native portal-based pickers can't be used here — deep modals trap
  // focus and create stacking contexts that break them (see header
  // comment on this picker). The multi-select uses an inline expanding
  // panel rendered in-flow below the chip box. State is staged inside
  // the panel until the user clicks "Add" so quick browses don't
  // commit partial selections.
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerSearch, setPickerSearch] = useState('');
  const [pickerStaged, setPickerStaged] = useState<Set<string>>(() => new Set());

  // Reset staged selection + search when (re-)opening the panel so it
  // always starts empty. Also closes the panel when no unselected
  // ledgers remain.
  const openPicker = useCallback(() => {
    setPickerStaged(new Set());
    setPickerSearch('');
    setPickerOpen(true);
  }, []);
  const closePicker = useCallback(() => {
    setPickerOpen(false);
    setPickerStaged(new Set());
    setPickerSearch('');
  }, []);

  // Filter unselected ledgers by the panel's search term (case-insensitive).
  const filteredUnselected = useMemo(() => {
    const q = pickerSearch.trim().toLowerCase();
    if (!q) return unselectedLedgers;
    return unselectedLedgers.filter(l =>
      l.ledgerName.toLowerCase().includes(q),
    );
  }, [unselectedLedgers, pickerSearch]);

  const filteredIds = useMemo(
    () => filteredUnselected.map(l => String(l.ledgerId)),
    [filteredUnselected],
  );
  const allFilteredStaged = filteredIds.length > 0
    && filteredIds.every(id => pickerStaged.has(id));

  const toggleStaged = useCallback((id: string) => {
    setPickerStaged(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  // "Select all" toggles the entire CURRENTLY-VISIBLE list (i.e. honours
  // the search filter). Lets the user filter to a subset and add them
  // all in one click.
  const toggleAllFiltered = useCallback(() => {
    setPickerStaged(prev => {
      const next = new Set(prev);
      if (allFilteredStaged) {
        for (const id of filteredIds) next.delete(id);
      } else {
        for (const id of filteredIds) next.add(id);
      }
      return next;
    });
  }, [allFilteredStaged, filteredIds]);

  const commitStaged = useCallback(() => {
    if (pickerStaged.size === 0) {
      closePicker();
      return;
    }
    const next = new Set(selectedIds);
    for (const id of pickerStaged) next.add(id);
    onChange(next);
    closePicker();
  }, [pickerStaged, selectedIds, onChange, closePicker]);

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0, gap: 4 }}>
      <div style={ledgerChipBoxStyle}>
        {selectedLedgers.map(l => {
          const id = String(l.ledgerId);
          return (
            <span
              key={id}
              style={ledgerChipStyle}
              title={`${l.ledgerName} · CY ₹ ${fmtAmtLocal(l.closingActual * sign)} · PY ₹ ${fmtAmtLocal(l.openingActual * sign)}`}
            >
              <span style={ledgerChipLabelStyle}>{l.ledgerName}</span>
              <span style={ledgerChipAmountStyle}>
                ₹ {fmtAmtLocal(l.closingActual * sign)}
              </span>
              <button
                type="button"
                onClick={() => removeLedger(id)}
                style={ledgerChipRemoveStyle}
                title={`Remove ${l.ledgerName}`}
                aria-label={`Remove ${l.ledgerName}`}
              >
                ×
              </button>
            </span>
          );
        })}

        {availableLedgers.length === 0 ? (
          <span style={ledgerChipEmptyStyle}>
            No unallocated ledgers under this class.
          </span>
        ) : unselectedLedgers.length > 0 ? (
          <button
            type="button"
            style={ledgerChipAddBtnStyle}
            onClick={pickerOpen ? closePicker : openPicker}
            title="Pick one or more ledgers"
          >
            {pickerOpen
              ? '× Close'
              : selectedLedgers.length === 0
                ? `+ Select ledgers (${unselectedLedgers.length} available)`
                : `+ Add ledgers (${unselectedLedgers.length} more)`}
          </button>
        ) : (
          <span style={ledgerChipAllPickedStyle}>
            All ledgers under this class are selected.
          </span>
        )}
      </div>

      {pickerOpen && unselectedLedgers.length > 0 && (
        <div style={pickerPanelStyle}>
          <div style={pickerHeaderRowStyle}>
            <label style={pickerSelectAllLabelStyle}>
              <input
                type="checkbox"
                checked={allFilteredStaged}
                onChange={toggleAllFiltered}
                disabled={filteredIds.length === 0}
              />
              <span>
                {allFilteredStaged ? 'Unselect all' : 'Select all'}
                {pickerSearch.trim() && filteredIds.length > 0
                  ? ` (${filteredIds.length} filtered)`
                  : ''}
              </span>
            </label>
            <input
              type="search"
              className="form-input"
              placeholder="Search ledger name…"
              value={pickerSearch}
              onChange={e => setPickerSearch(e.target.value)}
              style={pickerSearchInputStyle}
            />
          </div>

          <div style={pickerListStyle}>
            {filteredUnselected.length === 0 ? (
              <div style={pickerEmptyHintStyle}>
                {pickerSearch.trim()
                  ? `No ledger matches "${pickerSearch.trim()}".`
                  : 'No ledgers available.'}
              </div>
            ) : (
              filteredUnselected.map(l => {
                const id = String(l.ledgerId);
                const checked = pickerStaged.has(id);
                return (
                  <label
                    key={id}
                    style={{
                      ...pickerRowStyle,
                      ...(checked ? pickerRowStagedStyle : null),
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggleStaged(id)}
                    />
                    <span style={pickerRowNameStyle}>{l.ledgerName}</span>
                    <span style={pickerRowAmountStyle}>
                      ₹ {fmtAmtLocal(l.closingActual * sign)}
                    </span>
                  </label>
                );
              })
            )}
          </div>

          <div style={pickerFooterStyle}>
            <span style={pickerStagedCountStyle}>
              {pickerStaged.size === 0
                ? 'No ledgers selected'
                : `${pickerStaged.size} selected`}
            </span>
            <button
              type="button"
              className="btn btn-secondary btn-sm"
              onClick={closePicker}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-primary btn-sm"
              onClick={commitStaged}
              disabled={pickerStaged.size === 0}
            >
              Add {pickerStaged.size > 0 ? `(${pickerStaged.size})` : ''}
            </button>
          </div>
        </div>
      )}

      {selectedIds.size > 0 && (
        <div style={ledgerChipSummaryStyle}>
          {selectedIds.size} ledger{selectedIds.size === 1 ? '' : 's'} ·
          CY ₹ {fmtAmtLocal(selectedSum.cy * sign)} ·
          PY ₹ {fmtAmtLocal(selectedSum.py * sign)}
        </div>
      )}
    </div>
  );
};

// ----------------------------------------------------------------------
// Local helpers (independent of MainNotePane so the file is self-contained)
// ----------------------------------------------------------------------

function fmtAmtLocal(v: number): string {
  return v.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}


// ============================================================================
// Styles — verbatim from MainNotePane so the editor visual is identical
// ============================================================================

const editorContainerStyle: React.CSSProperties = {
  marginTop: 10,
  padding: 12,
  border: '1px solid var(--form-border-color)',
  borderRadius: 6,
  background: 'var(--bg-primary)',
  maxWidth: 560,
};
const editorTitleStyle: React.CSSProperties = {
  fontSize: '0.82rem',
  fontWeight: 700,
  color: 'var(--text-primary)',
  marginBottom: 8,
};
const editorRowStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  marginBottom: 6,
};
const editorLabelStyle: React.CSSProperties = {
  width: 110,
  fontSize: '0.78rem',
  color: 'var(--text-secondary)',
  fontWeight: 500,
};
const editorInputStyle: React.CSSProperties = {
  flex: 1,
  fontSize: '0.82rem',
  padding: '4px 8px',
};
const editorFooterStyle: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'flex-end',
  gap: 8,
  marginTop: 10,
  paddingTop: 8,
  borderTop: '1px solid var(--border-color)',
};
const radioLabelStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 4,
  cursor: 'pointer',
};
const requiredStarStyle: React.CSSProperties = { color: '#dc2626' };
const crossNoteSectionStyle: React.CSSProperties = {
  marginTop: 8,
  padding: 8,
  borderRadius: 4,
  background: 'var(--bg-secondary)',
  border: '1px dashed var(--form-border-color)',
};
const renameOnlyHintStyle: React.CSSProperties = {
  marginBottom: 8,
  padding: '6px 10px',
  borderRadius: 4,
  background: 'var(--bg-secondary)',
  border: '1px dashed var(--form-border-color)',
  fontSize: '0.75rem',
  color: 'var(--text-secondary)',
  fontStyle: 'italic',
};
// LedgerChipPicker styles ----------------------------------------------------

const ledgerChipBoxStyle: React.CSSProperties = {
  flex: 1,
  minWidth: 0,
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  gap: 6,
  padding: 6,
  border: '1px solid var(--form-border-color)',
  borderRadius: 4,
  minHeight: 36,
  background: 'var(--bg-primary)',
};
const ledgerChipStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  padding: '2px 4px 2px 10px',
  borderRadius: 999,
  background: 'var(--bg-secondary)',
  border: '1px solid var(--form-border-color)',
  color: 'var(--text-primary)',
  fontSize: '0.78rem',
  fontWeight: 500,
  maxWidth: 320,
};
const ledgerChipLabelStyle: React.CSSProperties = {
  maxWidth: 180,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};
const ledgerChipAmountStyle: React.CSSProperties = {
  fontVariantNumeric: 'tabular-nums',
  color: 'var(--text-secondary)',
  fontSize: '0.72rem',
};
const ledgerChipRemoveStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  border: 'none',
  background: 'transparent',
  cursor: 'pointer',
  color: 'var(--text-secondary)',
  fontSize: '1rem',
  lineHeight: 1,
  width: 18,
  height: 18,
  borderRadius: 999,
  padding: 0,
};
const ledgerChipAddBtnStyle: React.CSSProperties = {
  flex: '0 1 auto',
  border: '1px dashed var(--form-border-color)',
  background: 'transparent',
  fontSize: '0.78rem',
  fontWeight: 500,
  outline: 'none',
  cursor: 'pointer',
  color: 'var(--text-secondary)',
  padding: '4px 10px',
  borderRadius: 4,
};
const ledgerChipEmptyStyle: React.CSSProperties = {
  fontSize: '0.78rem',
  color: 'var(--text-secondary)',
  padding: '4px 6px',
};
const ledgerChipAllPickedStyle: React.CSSProperties = {
  fontSize: '0.72rem',
  color: 'var(--text-secondary)',
  padding: '2px 6px',
};
const ledgerChipSummaryStyle: React.CSSProperties = {
  marginLeft: 4,
  fontSize: '0.72rem',
  color: 'var(--text-secondary)',
  fontVariantNumeric: 'tabular-nums',
};

// Multi-select picker panel — inline expansion below the chip box. No
// portal (would break inside the deep-modal stacking context), so the
// panel lives in the editor's own flow. Bordered + slightly shaded so
// it visually reads as a "drawer" rather than another row of the form.
const pickerPanelStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 6,
  marginTop: 4,
  padding: 8,
  border: '1px solid var(--form-border-color)',
  borderRadius: 4,
  background: 'var(--bg-secondary)',
};
const pickerHeaderRowStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  flexWrap: 'wrap',
};
const pickerSelectAllLabelStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  fontSize: '0.78rem',
  fontWeight: 600,
  color: 'var(--text-primary)',
  cursor: 'pointer',
  userSelect: 'none',
};
const pickerSearchInputStyle: React.CSSProperties = {
  flex: 1,
  minWidth: 160,
  fontSize: '0.78rem',
  padding: '3px 8px',
};
const pickerListStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  maxHeight: 220,
  overflowY: 'auto',
  border: '1px solid var(--form-border-color)',
  borderRadius: 4,
  background: 'var(--bg-primary)',
};
const pickerRowStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  padding: '4px 8px',
  borderBottom: '1px solid var(--border-color)',
  fontSize: '0.78rem',
  color: 'var(--text-primary)',
  cursor: 'pointer',
};
const pickerRowStagedStyle: React.CSSProperties = {
  background: 'rgba(37, 99, 235, 0.08)',
};
const pickerRowNameStyle: React.CSSProperties = {
  flex: 1,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};
const pickerRowAmountStyle: React.CSSProperties = {
  fontVariantNumeric: 'tabular-nums',
  color: 'var(--text-secondary)',
  fontSize: '0.72rem',
};
const pickerEmptyHintStyle: React.CSSProperties = {
  padding: '10px 8px',
  fontSize: '0.78rem',
  color: 'var(--text-secondary)',
  fontStyle: 'italic',
  textAlign: 'center',
};
const pickerFooterStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  paddingTop: 4,
};
const pickerStagedCountStyle: React.CSSProperties = {
  flex: 1,
  fontSize: '0.72rem',
  color: 'var(--text-secondary)',
};

export default LineItemEditor;
